/** The page viewer: OpenSeadragon reading the API's IIIF Image service (tiles at any zoom), with the boxes layer. */
import OpenSeadragon from 'openseadragon';
import type { PageRef } from '@imagesync/core';
import { Boxes } from './boxes.js';
import { $, normRot } from './util.js';

export let viewer: OpenSeadragon.Viewer | null = null;
export let boxes: Boxes | null = null;
let openToken = 0;

export interface ViewerHooks { opened: () => void; failed: (message: string) => void; rotated: () => void; moved: () => void; boxesChanged: () => void; selectionChanged: () => void }

export function initViewer(h: ViewerHooks): void {
  viewer = OpenSeadragon({
    element: $('#stage'),
    drawer: 'canvas',
    showNavigationControl: false,
    showNavigator: true, navigatorPosition: 'BOTTOM_RIGHT', navigatorSizeRatio: 0.15, navigatorAutoFade: false,
    visibilityRatio: 0.4, minZoomImageRatio: 0.4, maxZoomPixelRatio: 3,
    animationTime: 0.45, blendTime: 0, imageLoaderLimit: 6, preserveImageSizeOnResize: true, immediateRender: false,
    crossOriginPolicy: false, ajaxWithCredentials: true, loadTilesWithAjax: false,
    gestureSettingsMouse: { clickToZoom: false, dblClickToZoom: true },
    gestureSettingsTouch: { pinchRotate: false },
    gestureSettingsPen: { clickToZoom: false },
  } as OpenSeadragon.Options);
  viewer.addHandler('open', () => h.opened());
  viewer.addHandler('open-failed', () => h.failed('This page image couldn’t be opened. Reload the page and try again.'));
  viewer.addHandler('rotate', () => h.rotated());
  viewer.addHandler('animation-finish', () => h.moved());
  boxes = new Boxes(viewer, $('#stage'), h.boxesChanged, h.selectionChanged);
}

/** Open a page from its IIIF service. Returns false when a newer open has replaced this one. */
export async function openPage(p: PageRef, publicCopy = false): Promise<boolean> {
  if (!viewer) return false;
  const token = ++openToken;
  boxes?.clearAll();
  const base = publicCopy ? p.iiif.replace(/~b\d+$/, '~p' + p.publicRev) : p.iiif;
  // fetch info.json ourselves first, so a "being updated" or "not allowed" answer shows as a message, not a broken image
  const r = await fetch(base + '/info.json', { credentials: 'same-origin' }).catch(() => null);
  if (token !== openToken) return false;
  if (!r || !r.ok) {
    let msg = 'This page couldn’t be opened.';
    try { const d = await r?.json(); if (d?.message) msg = d.message; } catch { /* not json */ }
    viewer.close();
    throw new Error(msg);
  }
  const info = await r.json();
  if (token !== openToken) return false;
  viewer.open(info);
  return true;
}
export function closeViewer(): void { openToken++; boxes?.clearAll(); viewer?.close(); }
export const hasImage = (): boolean => !!viewer && viewer.world.getItemCount() > 0;
export const rotation = (): number => (hasImage() ? normRot(viewer!.viewport.getRotation()) : 0);

export function fit(mode: 'page' | 'width', immediately = false): void {
  if (!viewer || !hasImage()) return;
  const b = viewer.world.getItemAt(0).getBounds(), c = b.getCenter();
  const rot = rotation(), swap = rot % 180 !== 0;
  const we = swap ? b.height : b.width, he = swap ? b.width : b.height;
  const cs = viewer.viewport.getContainerSize(), ar = cs.y / Math.max(cs.x, 1);
  let z: number, target = c;
  if (mode === 'width') {
    z = 0.97 / we;
    if (rot === 0) { const visH = ar / z; target = new OpenSeadragon.Point(c.x, Math.min(c.y, b.y + visH / 2)); }
  } else z = Math.min(1 / we, ar / he) * 0.94;
  viewer.viewport.zoomTo(z, undefined, immediately);
  viewer.viewport.panTo(target, immediately);
}
export function zoomBy(f: number): void { if (viewer && hasImage()) { viewer.viewport.zoomBy(f); viewer.viewport.applyConstraints(); } }
export function setRotation(d: number): void { if (viewer && hasImage()) { viewer.viewport.setRotation(normRot(d), true); fit('page', true); } }
export function rotateBy(d: number): void { setRotation(rotation() + d); }

export function applyFilters(gray: boolean, invert: boolean): void {
  const f = [gray ? 'grayscale(1)' : '', invert ? 'invert(1)' : ''].filter(Boolean).join(' ') || 'none';
  if (!viewer) return;
  const canvases = [(viewer.drawer as unknown as { canvas?: HTMLCanvasElement }).canvas, (viewer.navigator as unknown as { drawer?: { canvas?: HTMLCanvasElement } })?.drawer?.canvas];
  for (const cv of canvases) if (cv?.style) cv.style.filter = f;
}

/** The part of the page on screen, in full-size image pixels (for "print the area on screen"). */
export function currentRegion(): { x: number; y: number; w: number; h: number; rotation: number } | null {
  if (!viewer || !hasImage()) return null;
  const item = viewer.world.getItemAt(0), size = item.getContentSize();
  const r = item.viewportToImageRectangle(viewer.viewport.getBounds(true)).getBoundingBox();
  const x = Math.max(0, Math.floor(r.x)), y = Math.max(0, Math.floor(r.y));
  const x2 = Math.min(size.x, Math.ceil(r.x + r.width)), y2 = Math.min(size.y, Math.ceil(r.y + r.height));
  if (x2 - x < 8 || y2 - y < 8) return null;
  return { x, y, w: x2 - x, h: y2 - y, rotation: rotation() };
}
export function viewState(): { zoom: number; center: OpenSeadragon.Point } | null {
  return viewer && hasImage() ? { zoom: viewer.viewport.getZoom(true), center: viewer.viewport.getCenter(true) } : null;
}
export function restoreView(v: { zoom: number; center: OpenSeadragon.Point } | null): void {
  if (!viewer || !hasImage()) return;
  if (v) { viewer.viewport.zoomTo(v.zoom, undefined, true); viewer.viewport.panTo(v.center, true); } else fit('page', true);
}
